import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import NotificationItem from "./NotificationItem/NotificationItem";
import notificationBell from "../../../assets/notificationIcon.svg";
import close from "../../../assets/close.svg";

import "./Notifications.scss";
import { INotification } from "../../../common/interfaces/notifications.interface";
import { UserContext } from "../../../contexts/userContext";
import { createNotification } from "../../../utilities/notification";
import { MESSAGE_TYPE } from "../../../common/constants/common.constants";
import {
  getAllNotifications,
  markAllNotifications,
} from "../../../api/notifications.api";
import { NotificationStatus } from "../../../common/enums/notification.enum";
import { isMobile } from "react-device-detect";

const Notifications = () => {
  const [allNotifications, setAllNotifications] = useState<INotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<
    INotification[]
  >([]);

  const [activeNotifications, setActiveNotifications] = useState(
    NotificationStatus.AllNotifications
  );

  const {
    notifications,
    setNotifications,
    toggleNotificationsModal,
    user,
    isXnft,
  } = useContext(UserContext);

  useEffect(() => {
    setAllNotifications(notifications ?? []);
    setUnreadNotifications(notifications?.filter((n) => !n.hasRead) ?? []);
  }, [notifications]);

  const renderNotificationsList = useMemo(() => {
    return (
      activeNotifications === NotificationStatus.AllNotifications
        ? allNotifications
        : unreadNotifications
    ).map((notification) => {
      return (
        <NotificationItem
          notification={notification}
          key={notification.userNotId}
        />
      );
    });
  }, [
    activeNotifications,
    allNotifications,
    unreadNotifications,
    notifications,
  ]);

  const onClose = () => {
    toggleNotificationsModal(false);
  };

  const markAllAsRead = useCallback(async () => {
    try {
      if (user) {
        await markAllNotifications(user.pubKey);
        const newNotifications = await getAllNotifications(user.pubKey);
        setNotifications(newNotifications);
        setUnreadNotifications([]);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Failed to mark all as read");
    }
  }, [notifications]);

  return (
    <div className="notifications">
      <div className="notifications__header">
        <p className="notifications__title">Notifications</p>
        {isMobile ||
          (isXnft && (
            <img
              className="notifications__close-icon"
              src={close}
              alt="close icon"
              onClick={onClose}
            />
          ))}
        <div className="notifications__header-title">
          <div className="notifications__filters">
            <button
              onClick={() =>
                setActiveNotifications(NotificationStatus.AllNotifications)
              }
              className={`${
                activeNotifications === NotificationStatus.AllNotifications
                  ? "notifications__filter-button notifications__filter-button--active"
                  : "notifications__filter-button"
              }`}
            >
              All({allNotifications.length})
            </button>
            <button
              onClick={() =>
                setActiveNotifications(NotificationStatus.UnreadNotifications)
              }
              className={`${
                activeNotifications === NotificationStatus.UnreadNotifications
                  ? "notifications__filter-button notifications__filter-button--active"
                  : "notifications__filter-button"
              }`}
            >
              Unread({unreadNotifications.length})
            </button>
          </div>
          <button
            onClick={markAllAsRead}
            className="notifications__mark_all_as_read"
          >
            Mark all as read
          </button>
        </div>
      </div>
      <div className="notifications__list">{renderNotificationsList}</div>
      {renderNotificationsList.length === 0 && (
        <div className="notifications__empty">
          <img src={notificationBell} alt="notificationBell" />
          <p>You don't have any notifications</p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
